import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Table,
  Spinner,
  Alert,
  Modal,
  Form,
  Dropdown,
  ButtonGroup,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc, orderBy, limit, startAfter } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function ClockInClockOutPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [displayName, setDisplayName] = useState("User");
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockEntries, setClockEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dayTotal, setDayTotal] = useState({ hours: 0, minutes: 0 });
  const [weekTotal, setWeekTotal] = useState({ hours: 0, minutes: 0 });
  const [dayBreakTotal, setDayBreakTotal] = useState(0);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  // Removed custom break modal state and custom break minutes state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const timerRef = useRef(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  
  // Break countdown states
  const [breakCountdown, setBreakCountdown] = useState(null);
  const [breakCountdownSeconds, setBreakCountdownSeconds] = useState(0);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const breakTimerRef = useRef(null);

  // Filter states
  const [filterType, setFilterType] = useState('all'); // 'all', 'day', 'week', 'month', 'custom'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'duration', 'breaks'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canClockInOut: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canClockInOut && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions, currentUserRole]);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            setCurrentUser(user);
            await fetchUserFullName(user.email);
            await fetchClockEntries(user.email);
            await checkClockInStatus(user.email);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error("Error in fetchUserData:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (isClockedIn) {
      startTimer();
    } else {
      stopTimer();
      setTimerSeconds(0);
    }
    return () => stopTimer();
  }, [isClockedIn]);

  // Break countdown effect
  useEffect(() => {
    if (isOnBreak && breakCountdownSeconds > 0) {
      breakTimerRef.current = setInterval(() => {
        setBreakCountdownSeconds((prev) => {
          if (prev <= 1) {
            handleBreakEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
        breakTimerRef.current = null;
      }
    }
    return () => {
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
        breakTimerRef.current = null;
      }
    };
  }, [isOnBreak, breakCountdownSeconds]);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      setTimerSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const fetchUserFullName = async (email) => {
    try {
      const usersQuery = query(collection(db, "Users"), where("email", "==", email));
      const querySnapshot = await getDocs(usersQuery);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setDisplayName(userData.fullName || "User");
        setProfileImageUrl(userData.profileImageUrl || null);
      }
    } catch (error) {
      console.error("Error fetching user full name:", error);
    }
  };

  const fetchClockEntries = async (email) => {
    try {
      const entriesQuery = query(
        collection(db, "clock_entries"),
        where("email", "==", email)
      );
      const querySnapshot = await getDocs(entriesQuery);
      const entries = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClockEntries(entries);

      calculateTotals(entries);
      updateTimerFromEntries(entries);
    } catch (error) {
      console.error("Error fetching clock entries:", error);
      setError("Failed to fetch clock entries.");
    }
  };

  // Apply filters when clock entries or filter settings change
  useEffect(() => {
    applyFilters();
  }, [clockEntries, filterType, sortBy, sortOrder, customDateRange]);

  const updateTimerFromEntries = (entries) => {
    // Calculate elapsed seconds from latest clock-in entry without clock-out
    const latestEntry = entries.find((entry) => entry.clockOut === null);
    if (latestEntry) {
      const clockInTime = new Date(latestEntry.clockIn).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - clockInTime) / 1000);
      setTimerSeconds(elapsedSeconds);
    } else {
      setTimerSeconds(0);
    }
  };

  const checkClockInStatus = async (email) => {
    try {
      const entriesQuery = query(
        collection(db, "clock_entries"),
        where("email", "==", email),
        where("clockOut", "==", null)
      );
      const querySnapshot = await getDocs(entriesQuery);
      setIsClockedIn(!querySnapshot.empty);
    } catch (error) {
      console.error("Error checking clock-in status:", error);
      setError("Failed to check clock-in status.");
    }
  };

  const calculateTotals = (entries) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    let dailyMinutes = 0;
    let weeklyMinutes = 0;
    let dailyBreakMinutes = 0;

    entries.forEach((entry) => {
      const clockIn = new Date(entry.clockIn);
      const clockOut = entry.clockOut ? new Date(entry.clockOut) : now; // Use now if clockOut is null (ongoing shift)
      const breaks = entry.breaks || [];
      const totalBreakMinutes = breaks.reduce((sum, b) => sum + b.duration, 0);
      const duration = (clockOut - clockIn) / 1000 / 60 - totalBreakMinutes;

      if (clockIn >= today) {
        dailyMinutes += duration > 0 ? duration : 0;
        dailyBreakMinutes += totalBreakMinutes;
      }

      if (clockIn >= startOfWeek) {
        weeklyMinutes += duration > 0 ? duration : 0;
      }
    });

    setDayTotal({
      hours: Math.floor(dailyMinutes / 60),
      minutes: (dailyMinutes % 60).toFixed(2),
    });

    setWeekTotal({
      hours: Math.floor(weeklyMinutes / 60),
      minutes: (weeklyMinutes % 60).toFixed(2),
    });

    setDayBreakTotal(dailyBreakMinutes);
  };

  // Filter and sort functions
  const filterEntries = (entries, filterType, customRange) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filterType) {
      case 'day':
        return entries.filter(entry => {
          const entryDate = new Date(entry.clockIn);
          return entryDate >= today;
        });
      
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return entries.filter(entry => {
          const entryDate = new Date(entry.clockIn);
          return entryDate >= startOfWeek;
        });
      
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return entries.filter(entry => {
          const entryDate = new Date(entry.clockIn);
          return entryDate >= startOfMonth;
        });
      
      case 'custom':
        if (!customRange.startDate || !customRange.endDate) return entries;
        const startDate = new Date(customRange.startDate);
        const endDate = new Date(customRange.endDate);
        endDate.setHours(23, 59, 59, 999);
        return entries.filter(entry => {
          const entryDate = new Date(entry.clockIn);
          return entryDate >= startDate && entryDate <= endDate;
        });
      
      default:
        return entries;
    }
  };

  const sortEntries = (entries, sortBy, sortOrder) => {
    return [...entries].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.clockIn).getTime();
          bValue = new Date(b.clockIn).getTime();
          break;
        case 'duration':
          const aClockOut = a.clockOut ? new Date(a.clockOut) : new Date();
          const bClockOut = b.clockOut ? new Date(b.clockOut) : new Date();
          const aBreaks = (a.breaks || []).reduce((sum, b) => sum + b.duration, 0);
          const bBreaks = (b.breaks || []).reduce((sum, b) => sum + b.duration, 0);
          aValue = (aClockOut - new Date(a.clockIn)) / 1000 / 60 - aBreaks;
          bValue = (bClockOut - new Date(b.clockIn)) / 1000 / 60 - bBreaks;
          break;
        case 'breaks':
          aValue = (a.breaks || []).reduce((sum, b) => sum + b.duration, 0);
          bValue = (b.breaks || []).reduce((sum, b) => sum + b.duration, 0);
          break;
        default:
          return 0;
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const applyFilters = () => {
    const filtered = filterEntries(clockEntries, filterType, customDateRange);
    const sorted = sortEntries(filtered, sortBy, sortOrder);
    setFilteredEntries(sorted);
    setCurrentPage(1);
  };

  // Pagination functions
  const getPaginatedEntries = () => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return filteredEntries.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredEntries.length / entriesPerPage);
  };

  // Export functions
  const exportToCSV = () => {
    const headers = ['Date', 'Clock In', 'Clock Out', 'Duration (hours)', 'Breaks (minutes)', 'Status'];
    const csvContent = [
      headers.join(','),
      ...getPaginatedEntries().map(entry => {
        const clockIn = new Date(entry.clockIn);
        const clockOut = entry.clockOut ? new Date(entry.clockOut) : null;
        const breaks = (entry.breaks || []).reduce((sum, b) => sum + b.duration, 0);
        const duration = clockOut ? (clockOut - clockIn) / 1000 / 60 / 60 - (breaks / 60) : 0;
        
        return [
          clockIn.toLocaleDateString('en-GB'),
          clockIn.toLocaleTimeString(),
          clockOut ? clockOut.toLocaleTimeString() : 'Ongoing',
          duration.toFixed(2),
          breaks,
          entry.clockOut ? 'Completed' : 'Active'
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clock_entries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const jsonData = getPaginatedEntries().map(entry => ({
      id: entry.id,
      clockIn: entry.clockIn,
      clockOut: entry.clockOut,
      breaks: entry.breaks || [],
      duration: entry.clockOut ? 
        ((new Date(entry.clockOut) - new Date(entry.clockIn)) / 1000 / 60 / 60) - 
        ((entry.breaks || []).reduce((sum, b) => sum + b.duration, 0) / 60) : 0
    }));

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clock_entries_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    if (exportFormat === 'csv') {
      exportToCSV();
    } else {
      exportToJSON();
    }
    setShowExportModal(false);
  };

  const handleClockIn = async () => {
    if (window.confirm("Are you sure you want to clock in?")) {
      try {
        setError(null);
        const now = new Date();
        setIsClockedIn(true);

        await addDoc(collection(db, "clock_entries"), {
          email: currentUser.email,
          clockIn: now.toISOString(),
          clockOut: null,
          breaks: [], // Initialize with empty breaks array
        });

        await fetchClockEntries(currentUser.email);
        setSuccess("Successfully clocked in!");
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Error during clock-in:", error);
        setError("Failed to clock in. Please try again.");
        setIsClockedIn(false);
      }
    }
  };

  const handleClockOut = async () => {
    if (window.confirm("Are you sure you want to clock out?")) {
      try {
        setError(null);
        const now = new Date();
        setIsClockedIn(false);

        const latestEntry = clockEntries.find((entry) => entry.clockOut === null);
        if (latestEntry) {
          const docRef = doc(db, "clock_entries", latestEntry.id);
          await updateDoc(docRef, {
            clockOut: now.toISOString(),
          });
        } else {
          throw new Error("No active clock entry found");
        }

        await fetchClockEntries(currentUser.email);
        setSuccess("Successfully clocked out!");
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Error during clock-out:", error);
        setError("Failed to clock out. Please try again.");
        setIsClockedIn(true);
      }
    }
  };

  const handleBreak = async (duration) => {
    try {
      setError(null);
      const latestEntry = clockEntries.find((entry) => entry.clockOut === null);
      if (latestEntry) {
        // Start break countdown
        setIsOnBreak(true);
        setBreakCountdown(duration);
        setBreakCountdownSeconds(duration * 60); // Convert minutes to seconds
        
        setSuccess(`Break started! ${duration} minutes remaining.`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error("No active shift found. Please clock in first.");
      }
    } catch (error) {
      console.error("Error during break:", error);
      setError("Failed to start break. Please try again.");
    }
  };

  const handleBreakEnd = async () => {
    try {
      setError(null);
      const latestEntry = clockEntries.find((entry) => entry.clockOut === null);
      if (latestEntry && breakCountdown) {
        const docRef = doc(db, "clock_entries", latestEntry.id);
        const updatedBreaks = [...(latestEntry.breaks || []), { duration: breakCountdown }];
        await updateDoc(docRef, { breaks: updatedBreaks });
        await fetchClockEntries(currentUser.email);
        
        setSuccess(`Break completed! ${breakCountdown} minutes added to your break time.`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error("Error ending break:", error);
      setError("Failed to record break. Please try again.");
    } finally {
      // Reset break states
      setIsOnBreak(false);
      setBreakCountdown(null);
      setBreakCountdownSeconds(0);
    }
  };

  const handleCancelBreak = () => {
    if (window.confirm("Are you sure you want to cancel the current break?")) {
      setIsOnBreak(false);
      setBreakCountdown(null);
      setBreakCountdownSeconds(0);
      setSuccess("Break cancelled.");
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Removed handleCustomBreak function as custom break is removed

  const formatTimer = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}h ${mins}m ${secs}s`;
  };

  const formatBreakCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg">
            <i className="ri-error-warning-line fs-1 mb-3"></i>
            <h6 className="fw-bold">Access Denied</h6>
            <p className="mb-0">You don't have permission to access this page.</p>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">Loading clock in/out system...</p>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
            <i className="ri-check-line me-2"></i>
            {success}
          </Alert>
        )}

        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-time-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Clock In & Clock Out</h1>
          </div>
          <p className="lead text-muted">Track your work hours and manage your shifts</p>
        </div>
        
        {/* Enhanced Date Header */}
        <Row className="mb-5">
          <Col xs={12}>
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
              <div
                className="text-white text-center py-4"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: "700",
                  fontSize: "1.5rem",
                  letterSpacing: "1.5px",
                }}
              >
                <i className="ri-calendar-line me-3"></i>
                {new Date().toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Enhanced User Info and Timer Row */}
        <Row className="g-3 g-lg-4 mb-5">
          {/* User Info Card */}
          <Col xs={12} lg={6}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-user-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">User Information</h5>
                </div>
              </div>
              <Card.Body className="d-flex flex-row align-items-center p-4">
                <div className="me-4">
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt="User"
                      className="rounded-circle shadow"
                      style={{ width: "80px", height: "80px", objectFit: "cover" }}
                    />
                  ) : (
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center shadow"
                         style={{ width: "80px", height: "80px" }}>
                      <i className="ri-user-line fs-2 text-primary"></i>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="mb-1 fw-bold text-dark">{displayName}</h4>
                  <p className="text-muted mb-0">{currentUser?.email}</p>
                  <small className="text-success fw-semibold">
                    <i className="ri-check-line me-1"></i>
                    Active User
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Current Shift Timer */}
          {isClockedIn && !isOnBreak && (
            <Col xs={12} lg={6}>
              <Card
                className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                style={{ 
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(145deg, #d1e7dd 0%, #badbcc 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                }}>
                <div className="bg-gradient-success text-white p-3" 
                     style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                  <div className="d-flex align-items-center">
                    <i className="ri-time-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">Current Shift</h5>
                  </div>
                </div>
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <i className="ri-timer-line fs-1 text-success mb-3"></i>
                  </div>
                  <h4 className="mb-3 text-success fw-bold">
                    Active Timer
                  </h4>
                  <div className="bg-white rounded-3 p-3 shadow-sm">
                    <p
                      className="mb-0 fw-bold"
                      style={{
                        fontSize: "2.5rem",
                        color: "#0f5132",
                        letterSpacing: "1.2px",
                      }}
                    >
                      {formatTimer(timerSeconds)}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}

          {/* Break Countdown Timer */}
          {isOnBreak && (
            <Col xs={12} lg={6}>
              <Card
                className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                style={{ 
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(145deg, #fff3cd 0%, #ffeaa7 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                }}>
                <div className="bg-gradient-warning text-white p-3" 
                     style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <div className="d-flex align-items-center">
                    <i className="ri-pause-circle-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">Break Time</h5>
                  </div>
                </div>
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <i className="ri-timer-2-line fs-1 text-warning mb-3"></i>
                  </div>
                  <h4 className="mb-3 text-warning fw-bold">
                    Break Countdown
                  </h4>
                  <div className="bg-white rounded-3 p-3 shadow-sm mb-3">
                    <p
                      className="mb-0 fw-bold"
                      style={{
                        fontSize: "3rem",
                        color: breakCountdownSeconds <= 60 ? "#dc3545" : "#856404",
                        letterSpacing: "1.2px",
                        transition: "color 0.3s ease"
                      }}
                    >
                      {formatBreakCountdown(breakCountdownSeconds)}
                    </p>
                    {breakCountdownSeconds <= 60 && breakCountdownSeconds > 0 && (
                      <small className="text-danger fw-semibold">
                        <i className="ri-alarm-warning-line me-1"></i>
                        Break ending soon!
                      </small>
                    )}
                    
                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="progress" style={{ height: "8px", borderRadius: "10px" }}>
                        <div 
                          className="progress-bar bg-warning" 
                          role="progressbar" 
                          style={{ 
                            width: `${((breakCountdown * 60 - breakCountdownSeconds) / (breakCountdown * 60)) * 100}%`,
                            transition: "width 1s ease"
                          }}
                        ></div>
                      </div>
                      <small className="text-muted mt-1 d-block">
                        {Math.round(((breakCountdown * 60 - breakCountdownSeconds) / (breakCountdown * 60)) * 100)}% complete
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleCancelBreak}
                    className="rounded-pill px-4 py-2 fw-semibold"
                  >
                    <i className="ri-close-line me-2"></i>
                    Cancel Break
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        {/* Enhanced Totals Cards Row */}
        <Row className="g-3 g-lg-4 mb-5">
          <Col xs={12} sm={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-calendar-line fs-5 me-2"></i>
                  <h6 className="mb-0 fw-bold">Day Total</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-time-line fs-1 text-primary mb-3"></i>
                <p className="mb-0 fs-3 fw-bold text-primary">
                  {dayTotal.hours}h {dayTotal.minutes}m
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-warning text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-pause-circle-line fs-5 me-2"></i>
                  <h6 className="mb-0 fw-bold">Break Time</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-pause-circle-line fs-1 text-warning mb-3"></i>
                <p className="mb-0 fs-3 fw-bold text-warning">{dayBreakTotal} min</p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-info text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-calendar-week-line fs-5 me-2"></i>
                  <h6 className="mb-0 fw-bold">Week Total</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-calendar-week-line fs-1 text-info mb-3"></i>
                <p className="mb-0 fs-3 fw-bold text-info">
                  {weekTotal.hours}h {weekTotal.minutes}m
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Shift Controls Row */}
        <Row className="g-3 g-lg-4 mb-5">
          <Col xs={12}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-primary text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-play-circle-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Shift Controls</h4>
                </div>
              </div>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center p-5">
                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  <Button
                    variant="success"
                    size="lg"
                    className="px-5 py-4 shadow-lg rounded-pill"
                    disabled={isClockedIn}
                    onClick={handleClockIn}
                    style={{
                      fontWeight: "700",
                      fontSize: "1.1rem",
                      minWidth: "180px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <i className="ri-play-circle-line me-2"></i>
                    Start Shift
                  </Button>
                  <Button
                    variant="danger"
                    size="lg"
                    className="px-5 py-4 shadow-lg rounded-pill"
                    disabled={!isClockedIn || isOnBreak}
                    onClick={handleClockOut}
                    style={{
                      fontWeight: "700",
                      fontSize: "1.1rem",
                      minWidth: "180px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <i className="ri-stop-circle-line me-2"></i>
                    End Shift
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Break Controls Row */}
        <Row className="g-3 g-lg-4 mb-5">
          <Col xs={12}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-info text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-pause-circle-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Break Controls</h4>
                </div>
              </div>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-center gap-3 flex-wrap mb-3">
                  <Button
                    variant="outline-info"
                    onClick={() => handleBreak(15)}
                    disabled={!isClockedIn || isOnBreak}
                    className="rounded-pill px-4 py-2 fw-semibold"
                    style={{ minWidth: "140px" }}
                  >
                    <i className="ri-time-line me-2"></i>
                    15 Min Break
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => handleBreak(30)}
                    disabled={!isClockedIn || isOnBreak}
                    className="rounded-pill px-4 py-2 fw-semibold"
                    style={{ minWidth: "140px" }}
                  >
                    <i className="ri-restaurant-line me-2"></i>
                    30 Min Lunch
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => handleBreak(15)}
                    disabled={!isClockedIn || isOnBreak}
                    className="rounded-pill px-4 py-2 fw-semibold"
                    style={{ minWidth: "140px" }}
                  >
                    <i className="ri-coffee-line me-2"></i>
                    15 Min Break
                  </Button>
                </div>
                <div className="text-center">
                  <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    {isOnBreak 
                      ? "Currently on break - countdown timer is active" 
                      : "You can add breaks after starting your shift"
                    }
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Previous Clock Entries with Filtering */}
        <Row className="g-3 g-lg-4">
          <Col xs={12}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-secondary text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="d-flex align-items-center">
                    <i className="ri-history-line fs-4 me-3"></i>
                    <h4 className="mb-0 fw-bold">Clock Entries History</h4>
                    {filteredEntries.length > 0 && (
                      <span className="badge bg-white text-secondary ms-3 px-3 py-2 rounded-pill">
                        {filteredEntries.length} entries
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setShowExportModal(true)}
                    className="rounded-pill px-3 py-2"
                  >
                    <i className="ri-download-line me-2"></i>
                    Export
                  </Button>
                </div>
              </div>
              
              {/* Filter and Sort Controls */}
              <Card.Body className="p-4 border-bottom">
                <Row className="g-3">
                  <Col xs={12} md={3}>
                    <Form.Label className="fw-semibold text-muted">
                      <i className="ri-filter-line me-1"></i>Filter by Time Period
                    </Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-primary" className="w-100">
                        {filterType === 'all' && 'All Entries'}
                        {filterType === 'day' && 'Today'}
                        {filterType === 'week' && 'This Week'}
                        {filterType === 'month' && 'This Month'}
                        {filterType === 'custom' && 'Custom Range'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setFilterType('all')}>
                          <i className="ri-list-check me-2"></i>All Entries
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterType('day')}>
                          <i className="ri-calendar-line me-2"></i>Today
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterType('week')}>
                          <i className="ri-calendar-week-line me-2"></i>This Week
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterType('month')}>
                          <i className="ri-calendar-month-line me-2"></i>This Month
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterType('custom')}>
                          <i className="ri-calendar-range-line me-2"></i>Custom Range
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  
                  {filterType === 'custom' && (
                    <>
                      <Col xs={12} md={3}>
                        <Form.Label className="fw-semibold text-muted">Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={customDateRange.startDate}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Label className="fw-semibold text-muted">End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={customDateRange.endDate}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </Col>
                    </>
                  )}
                  
                  <Col xs={12} md={3}>
                    <Form.Label className="fw-semibold text-muted">
                      <i className="ri-sort-desc me-1"></i>Sort by
                    </Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" className="w-100">
                        {sortBy === 'date' && 'Date'}
                        {sortBy === 'duration' && 'Duration'}
                        {sortBy === 'breaks' && 'Break Time'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setSortBy('date')}>
                          <i className="ri-calendar-line me-2"></i>Date
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setSortBy('duration')}>
                          <i className="ri-time-line me-2"></i>Duration
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setSortBy('breaks')}>
                          <i className="ri-pause-circle-line me-2"></i>Break Time
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  
                  <Col xs={12} md={3}>
                    <Form.Label className="fw-semibold text-muted">
                      <i className="ri-arrow-up-down-line me-1"></i>Order
                    </Form.Label>
                    <ButtonGroup className="w-100">
                      <Button
                        variant={sortOrder === 'desc' ? 'primary' : 'outline-primary'}
                        onClick={() => setSortOrder('desc')}
                        size="sm"
                      >
                        <i className="ri-arrow-down-line me-1"></i>Desc
                      </Button>
                      <Button
                        variant={sortOrder === 'asc' ? 'primary' : 'outline-primary'}
                        onClick={() => setSortOrder('asc')}
                        size="sm"
                      >
                        <i className="ri-arrow-up-line me-1"></i>Asc
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </Card.Body>

              {/* Entries Table */}
              <Card.Body className="p-0">
                {filteredEntries.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table striped hover responsive className="mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th><i className="ri-hashtag me-1"></i>#</th>
                            <th><i className="ri-calendar-line me-1"></i>Date</th>
                            <th><i className="ri-login-circle-line me-1"></i>Clock In</th>
                            <th><i className="ri-logout-circle-line me-1"></i>Clock Out</th>
                            <th><i className="ri-pause-circle-line me-1"></i>Breaks</th>
                            <th><i className="ri-time-line me-1"></i>Duration</th>
                            <th><i className="ri-checkbox-circle-line me-1"></i>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedEntries().map((entry, index) => {
                            const clockInDate = new Date(entry.clockIn);
                            const clockOutDate = entry.clockOut ? new Date(entry.clockOut) : null;
                            const breaks = entry.breaks || [];
                            const breakMinutes = breaks.reduce((sum, b) => sum + b.duration, 0);
                            const duration = clockOutDate ? 
                              (clockOutDate - clockInDate) / 1000 / 60 - breakMinutes : 0;

                            return (
                              <tr key={entry.id}>
                                <td>
                                  <span className="badge bg-primary rounded-pill">
                                    {(currentPage - 1) * entriesPerPage + index + 1}
                                  </span>
                                </td>
                                <td className="fw-semibold">
                                  {clockInDate.toLocaleDateString('en-GB')}
                                </td>
                                <td className="fw-semibold">
                                  {clockInDate.toLocaleTimeString()}
                                </td>
                                <td className="fw-semibold">
                                  {clockOutDate ? clockOutDate.toLocaleTimeString() : 
                                    <Badge bg="warning" className="text-dark">Ongoing</Badge>
                                  }
                                </td>
                                <td>
                                  <Badge bg="info" className="text-white">
                                    {breakMinutes} min
                                  </Badge>
                                </td>
                                <td>
                                  {duration > 0 ? (
                                    <Badge bg="success">
                                      {Math.floor(duration / 60)}h {(duration % 60).toFixed(0)}m
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">N/A</Badge>
                                  )}
                                </td>
                                <td>
                                  {entry.clockOut ? (
                                    <Badge bg="success">
                                      <i className="ri-check-line me-1"></i>Completed
                                    </Badge>
                                  ) : (
                                    <Badge bg="warning" className="text-dark">
                                      <i className="ri-play-circle-line me-1"></i>Active
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-4 border-top">
                        <div className="text-muted">
                          Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, filteredEntries.length)} of {filteredEntries.length} entries
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                          >
                            <i className="ri-arrow-left-line me-1"></i>Previous
                          </Button>
                          <span className="px-3 py-2 text-muted">
                            Page {currentPage} of {getTotalPages()}
                          </span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))}
                            disabled={currentPage === getTotalPages()}
                          >
                            Next<i className="ri-arrow-right-line ms-1"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="ri-history-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-3">No Clock Entries Found</h5>
                    <p className="text-muted">
                      {filterType === 'all' 
                        ? 'Start your first shift to see your clock entries here.'
                        : 'No entries found for the selected filter criteria.'
                      }
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Export Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-download-line me-2"></i>
            Export Clock Entries
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Export Format</Form.Label>
              <Form.Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="csv">CSV (Excel Compatible)</option>
                <option value="json">JSON (Data Format)</option>
              </Form.Select>
            </Form.Group>
            <div className="alert alert-info">
              <i className="ri-information-line me-2"></i>
              <strong>Note:</strong> This will export the currently filtered and sorted entries. 
              {filteredEntries.length} entries will be exported.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExport}>
            <i className="ri-download-line me-2"></i>
            Export {exportFormat.toUpperCase()}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </React.Fragment>
  );
}
